import { Tldraw, getSnapshot, loadSnapshot } from 'tldraw'
import 'tldraw/tldraw.css'

// Hide tldraw's bottom-left zoom pill (the "50% >" control). The rest of
// tldraw's UI — top page bar, toolbar, style panel — stays.
const components = {
  NavigationPanel: null,
}

/**
 * Whiteboard editor backed by tldraw (MIT). Persists the full canvas state
 * as `{ tldraw: <snapshot> }` in `documents.body jsonb`. The snapshot is
 * tldraw's native serialised store — drawing, shapes, arrows, text, etc.
 *
 * The component remounts when `docId` changes (via `key`), so switching
 * between whiteboards yields a fresh tldraw instance with the right
 * snapshot loaded.
 */
export function WhiteboardEditor({ initialBody, docId, onSave }) {
  return (
    <div
      className="rounded-lg border border-[color:var(--color-rule)] overflow-hidden h-full relative"
      // `isolation: isolate` opens a new stacking context so tldraw's high
      // internal z-indexes (page bar, zoom pill, style panel) stay confined
      // to this container and can't punch through app-level modal/menu
      // overlays rendered via portals to <body>.
      style={{ minHeight: '75vh', isolation: 'isolate' }}
    >
      <Tldraw
        key={docId}
        components={components}
        onMount={(ed) => mountEditor(ed, initialBody, onSave)}
      />
    </div>
  )
}

function mountEditor(editor, initialBody, onSave) {
  // Load any saved snapshot.
  const snap = parseBody(initialBody)
  if (snap) {
    try {
      loadSnapshot(editor.store, snap)
    } catch (e) {
      console.warn('Whiteboard: could not load snapshot', e)
    }
  }

  // Debounced auto-save: 800ms after the last user change. Listening with
  // `source: 'user'` ignores remote / programmatic changes (e.g. our own
  // load above) so we don't loop.
  let timer = null
  let lastSaved = ''
  const dispose = editor.store.listen(
    () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        const next = getSnapshot(editor.store)
        const serialised = JSON.stringify(next)
        if (serialised === lastSaved) return
        lastSaved = serialised
        onSave({ tldraw: next })
      }, 800)
    },
    { source: 'user', scope: 'document' },
  )

  // The onMount callback's return is treated as a cleanup — tldraw will
  // call it when the editor unmounts.
  return () => {
    if (timer) clearTimeout(timer)
    dispose()
  }
}

function parseBody(body) {
  if (!body || typeof body !== 'object') return null
  if (body.tldraw && typeof body.tldraw === 'object') return body.tldraw
  return null
}
